import { createContext, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import Api from 'entities/helpers/api';
import EverTrue from 'app';
import ErrorLogger from 'entities/helpers/error-log-helper';
import { useIdentity } from 'base/identity-resolver';
import { createEmailSearchFunction } from '../utils/email-search-utils';

const ScheduledEmailContext = createContext();

const LIMIT = 20;

const ScheduledEmailProvider = ({ children }) => {
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduledTotal, setScheduledTotal] = useState(0);
  const [hasEverHadData, setHasEverHadData] = useState(false);
  const [page, setPage] = useState(1);
  const { user = {} } = useIdentity();
  const [dateRange, setDateRange] = useState(null);
  const [selectedEmailAddresses, setSelectedEmailAddresses] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);

  const getAllScheduledByUser = async () => {
    setLoading(true);
    try {
      const res = await Api.OUTREACH.SCHEDULED.get({
        params: {
          offset: (page - 1) * LIMIT,
          limit: LIMIT,
        },
      });
      const { items, total } = res;
      setScheduledEmails(items);
      setScheduledTotal(total);
      if (items?.length > 0) {
        setHasEverHadData(true);
      }
    } catch (e) {
      ErrorLogger.error('Error getting scheduled emails', { extra: { e, page_name: 'Outreach Scheduled Emails' } });
    } finally {
      setLoading(false);
    }
  };

  const createScheduled = async (payload) => {
    setLoading(true);
    try {
      const res = await Api.OUTREACH.SCHEDULED.post({
        data: JSON.stringify(payload),
      });
      EverTrue.Alert.success(`Successfully created scheduled send.`);

      const isDateRangeValid = dateRange?.from !== null && dateRange?.to !== null;
      const isFilterApplied = isDateRangeValid || selectedEmailAddresses?.length > 0 || selectedNames?.length > 0;

      if (isFilterApplied) {
        await searchScheduled({ dateRange, emailRecipients: selectedEmailAddresses, selectedNames });
      } else {
              setScheduledEmails([res, ...scheduledEmails]);
      setScheduledTotal((prev) => prev + 1);
      }
    } catch (e) {
      ErrorLogger.error('Error while creating scheduled send', {
        extra: { e, page_name: 'Outreach Scheduled Emails' },
      });
    } finally {
      EverTrue.track.set('outreach_action', { type: 'schedule_email' });
      setLoading(false);
    }
  };

  const updateScheduled = async (payload, id) => {
    setLoading(true);
    try {
      const res = await Api.OUTREACH.SCHEDULED_BY_ID.put({
        urlArgs: { id },
        data: JSON.stringify(payload),
      });
      EverTrue.Alert.success(`Successfully updated scheduled send.`);
      setScheduledEmails(scheduledEmails.map((scheduledEmail) => (scheduledEmail.id === id ? res : scheduledEmail)));
    } catch (e) {
      ErrorLogger.error('Error while updating scheduled send', {
        extra: { e, page_name: 'Outreach Scheduled Emails' },
      });
    } finally {
      setLoading(false);
    }
  };

  const bulkDeleteScheduled = async (selectedScheduledIds) => {
    try {
      await Api.OUTREACH.BULK_DELETE_SCHEDULED.delete({
        data: JSON.stringify(selectedScheduledIds),
      });
      EverTrue.Alert.success(`Successfully deleted scheduled sends.`);
      setPage(1);
      await getAllScheduledByUser();
    } catch (e) {
      ErrorLogger.error('Error while deleting email drafts', { extra: { error: e, page_name: 'Bulk Delete Drafts' } });
    }
  };

  const deleteScheduledById = async (scheduledEmailId) => {
    setLoading(true);
    try {
      await Api.OUTREACH.SCHEDULED_BY_ID.delete({
        urlArgs: { id: scheduledEmailId },
      });
      EverTrue.Alert.success(`Successfully deleted scheduled email.`);
      setScheduledEmails(scheduledEmails.filter((scheduledEmail) => scheduledEmail.id !== scheduledEmailId));
      setScheduledTotal((prev) => Math.max(0, prev - 1));
    } catch (e) {
      ErrorLogger.error('Error while deleting scheduled send', {
        extra: { error: e, page_name: 'Delete Scheduled Send' },
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelScheduled = async (scheduledEmailId) => {
    setLoading(true);
    try {
      const res = await Api.OUTREACH.CANCEL_SCHEDULED.put({
        urlArgs: { id: scheduledEmailId },
      });
      EverTrue.Alert.success(`Scheduled send canceled, and the message has been reverted to draft.`);
      setScheduledEmails(scheduledEmails.filter((scheduledEmail) => scheduledEmail.id !== scheduledEmailId));
      setScheduledTotal((prev) => Math.max(0, prev - 1));
      // Note: When a scheduled email is canceled, it becomes a draft
      // The draft count will be updated when the user navigates to the Drafts tab
      // or when the draft context is refreshed
      return res;
    } catch (e) {
      ErrorLogger.error('Error while cancelling scheduled send', {
        extra: { error: e, page_name: 'Cancel Scheduled Send' },
      });
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const searchScheduled = useCallback(
    async (filters) => {
      return createEmailSearchFunction(
        'scheduled',
        user,
        setScheduledEmails,
        setScheduledTotal,
        page,
        LIMIT,
        setLoading
      )(filters);
    },
    [page, user, setScheduledEmails, setScheduledTotal, setLoading]
  );

  const updateFilters = useCallback(({ dateRange, selectedEmailAddresses, selectedNames }) => {
    if (typeof dateRange !== 'undefined') setDateRange(dateRange);
    if (typeof selectedEmailAddresses !== 'undefined') setSelectedEmailAddresses(selectedEmailAddresses);
    if (typeof selectedNames !== 'undefined') setSelectedNames(selectedNames);
  }, []);

  return (
    <ScheduledEmailContext.Provider
      value={{
        scheduledEmails,
        loading,
        page,
        onPageChange,
        scheduledTotal,
        scheduledLimit: LIMIT,
        createScheduled,
        updateScheduled,
        bulkDeleteScheduled,
        deleteScheduledById,
        cancelScheduled,
        getAllScheduledByUser,
        searchScheduled,
        hasEverHadData,
        updateFilters,
      }}
    >
      {children}
    </ScheduledEmailContext.Provider>
  );
};

const useScheduledEmails = () => {
  const context = useContext(ScheduledEmailContext);
  if (!context) {
    throw new Error('useScheduledEmails must be used within a ScheduledEmailProvider');
  }
  return context;
};

ScheduledEmailProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { ScheduledEmailProvider, useScheduledEmails };
